import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import { useDispatch, useSelector } from "react-redux";
import useWindowSize from "../../../../hooks/useWindowSize";
import { getCustomFields } from "../../../../redux/slices/customFields";
import NotyfContext from "../../../../contexts/NotyfContext";
import { Button, Form, Modal, Table } from "react-bootstrap";
import Loader from "../../../../components/spinners/Loader";
import { Formik } from "formik";
import {
  getSimCustomFieldValues,
  updateSimCustomFieldValues,
} from "../../../../redux/slices/customFieldValues";
import { validateSimsForAction } from "../../../../redux/slices/sims";
import useDeviceType from "../../../../hooks/useDeviceType";

const CustomFieldValuesModal = ({
  selectedSims = [],
  showCustomFieldValuesModal,
  setShowCustomFieldValuesModal,
  clearSelectedRows,
}) => {
  const { backgroundColor, fontColor } = useWhitelabel();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const customFields = useSelector((state) => state.customFields.customFields);
  const customFieldValues = useSelector(
    (state) => state.customFieldValues.customFieldValues
  );
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const notyf = useContext(NotyfContext);

  const handleToggleModal = useCallback(() => {
    setShowCustomFieldValuesModal(false);
  }, [setShowCustomFieldValuesModal]);

  // 🔁 now 3 steps (1: Upload, 2: Choose values, 3: Summary)
  const [step, setStep] = useState(selectedSims?.length ? 2 : 1);
  const [fileInputKey, setFileInputKey] = React.useState(Date.now());

  // All SIM identifiers to operate on (internal ids when selected)
  const allSimIds = useMemo(() => {
    return selectedSims?.length ? selectedSims.map((s) => s.id) : [];
  }, [selectedSims]);

  // Prefill values when exactly one selected SIM
  const buildInitialFieldValues = useCallback(
    (idsForStep2) =>
      customFields.map((cf) => {
        const matchedValue = customFieldValues.find(
          (v) => Number(v.custom_field_id) === Number(cf.id)
        );
        return {
          customFieldId: cf.id,
          displayName: cf.displayName,
          value:
            idsForStep2.length === 1 && matchedValue ? matchedValue.value : "",
        };
      }),
    [customFields, customFieldValues]
  );

  // 🆕 Keep uploaded SIMs as full objects {id, imsi, iccid} for summary
  const initialFormikValues = useMemo(
    () => ({
      file: null,
      uploadedSims: [], // array of { id, imsi, iccid }
      values: buildInitialFieldValues(selectedSims?.length ? allSimIds : []),
    }),
    [buildInitialFieldValues, selectedSims, allSimIds]
  );

  useEffect(() => {
    setActiveUser(impersonationUser?.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchCustom = async () => {
      setIsLoading(true);
      await dispatch(getCustomFields()).catch((error) => {
        notyf.open({
          type: "danger",
          message: error,
          duration: 5000,
          ripple: true,
          dismissible: false,
          position: { x: "center", y: "top" },
        });
        handleToggleModal(false);
      });
    };

    const fetchSingleSimValues = async (simId) => {
      if (!simId) return;
      await dispatch(getSimCustomFieldValues({ sim_id: simId })).catch(
        (error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: { x: "center", y: "top" },
          });
        }
      );
    };

    if (
      activeUser.id &&
      activeUser.role !== "admin" &&
      showCustomFieldValuesModal
    ) {
      if (step === 2) {
        fetchCustom();
        if (selectedSims?.length === 1) {
          fetchSingleSimValues(selectedSims[0].id);
        }
        setTimeout(() => setIsLoading(false), 500);
      } else {
        setIsLoading(false);
      }
    }
  }, [
    activeUser,
    showCustomFieldValuesModal,
    dispatch,
    notyf,
    handleToggleModal,
    step,
    selectedSims,
  ]);

  useEffect(() => {
    if (showCustomFieldValuesModal) {
      setStep(selectedSims?.length ? 2 : 1);
      setFileInputKey(Date.now());
    }
  }, [showCustomFieldValuesModal, selectedSims]);

  if (!isDesktop) return null;

  // Helpers for summary
  const summarySims = (uploadedSims, selectedSims) =>
    selectedSims?.length
      ? selectedSims.map((s) => ({
          id: s.id,
          imsi: s.imsi || "",
          iccid: s.iccid || "",
        }))
      : uploadedSims;

  const renderSummaryTable = (rows, fields) => (
    <Table hover responsive>
      <thead>
        <tr>
          <th>IMSI</th>
          <th>ICCID</th>
          {fields.map((f) => (
            <th key={f.customFieldId}>{f.displayName}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.id}-${r.imsi}-${r.iccid}`}>
            <td>{r.imsi}</td>
            <td>{r.iccid}</td>
            {fields.map((f) => (
              <td key={`${r.id}-${f.customFieldId}`}>{f.value}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <Modal
      show={showCustomFieldValuesModal}
      onHide={() => handleToggleModal(false)}
      centered
      size="lg"
      backdrop="static"
      scrollable
    >
      <Formik
        enableReinitialize
        initialValues={initialFormikValues}
        onSubmit={async (values, formikHelpers) => {
          // Step 1 -> validate file, move to Step 2
          if (step === 1) {
            if (!values.uploadedSims || values.uploadedSims.length === 0) {
              formikHelpers.setFieldError(
                "file",
                "Please upload a CSV/XLSX with at least one IMSI/ICCID."
              );
              return;
            }
            setIsLoading(true);
            // For uploaded SIMs, no backend prefill; just build empty fields
            formikHelpers.setFieldValue(
              "values",
              buildInitialFieldValues(values.uploadedSims),
              false
            );
            setStep(2);
            setTimeout(() => setIsLoading(false), 300);
            return;
          }

          // Step 2 -> go to Step 3 (Summary)
          if (step === 2) {
            // basic check: at least one value field exists (they can be empty strings)
            setStep(3);
            return;
          }

          // Step 3 -> final submit to backend
          const simTargets = selectedSims?.length
            ? selectedSims.map((s) => s.id) // internal IDs
            : values.uploadedSims.map((s) => s.id); // validated SIM DB IDs

          if (!simTargets.length) {
            notyf.open({
              type: "danger",
              message: "No SIMs provided. Select SIMs or upload a CSV first.",
              duration: 5000,
              ripple: true,
              dismissible: false,
              position: { x: "center", y: "top" },
            });
            return;
          }

          await dispatch(
            updateSimCustomFieldValues({
              sim_ids: simTargets,
              values: values.values.map((v) => ({
                customFieldId: v.customFieldId,
                value: v.value,
              })),
            })
          )
            .then(() => {
              notyf.open({
                type: "success",
                message: "Custom Field Values successfully updated",
                duration: 5000,
                ripple: true,
                dismissible: false,
                position: { x: "center", y: "top" },
              });
              if (clearSelectedRows) clearSelectedRows();
            })
            .catch((error) => {
              notyf.open({
                type: "danger",
                message: error,
                duration: 5000,
                ripple: true,
                dismissible: false,
                position: { x: "center", y: "top" },
              });
            })
            .finally(() => {
              handleToggleModal(false);
            });
        }}
      >
        {({ values, setFieldValue, handleSubmit, errors, touched }) => (
          <>
            <Modal.Header>
              <Modal.Title>
                {step === 1
                  ? "Step 1 of 3: Select SIMs (CSV/XLSX)"
                  : step === 2
                  ? "Step 2 of 3: Custom Fields"
                  : "Step 3 of 3: Summary & Confirm"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {isLoading && <Loader />}

              {/* STEP 1: Upload */}
              {!isLoading && step === 1 && (
                <div>
                  <p className="mb-2">
                    No SIMs are selected. Upload a CSV/XLSX file with a header
                    column named <code>IMSI</code> or <code>ICCID</code>.
                  </p>

                  <Form.Group controlId="csvUpload" className="mb-3">
                    <Form.Label>Choose CSV/XLSX</Form.Label>
                    <Form.Control
                      key={fileInputKey}
                      type="file"
                      accept=".csv, .xlsx"
                      onChange={async (e) => {
                        const file = e.currentTarget.files?.[0] || null;
                        setFieldValue("file", file);
                        setFieldValue("uploadedSims", []);
                        if (!file) return;

                        await validateSimsForAction({
                          file,
                          action: "customField",
                        })
                          .then((response) => {
                            // 🆕 Expect backend to return [{id, imsi, iccid}, ...]
                            notyf.open({
                              type: "success",
                              message: "Sim file validated successfully",
                              duration: 5000,
                              ripple: true,
                              dismissible: false,
                              position: { x: "center", y: "top" },
                            });
                            setFieldValue("file", null);
                            setFieldValue("uploadedSims", response); // keep full objects
                            // Optionally auto-advance to Step 2:
                            setStep(2);
                          })
                          .catch((error) => {
                            notyf.open({
                              type: "danger",
                              message: error,
                              duration: 5000,
                              ripple: true,
                              dismissible: false,
                              position: { x: "center", y: "top" },
                            });
                            setFieldValue("file", null);
                            setFieldValue("uploadedSims", []);
                            setFileInputKey(Date.now());
                          });
                      }}
                      isInvalid={touched.file && !!errors.file}
                    />
                    {touched.file && errors.file && (
                      <div className="text-danger mt-2" role="alert">
                        {errors.file}
                      </div>
                    )}
                    {values.uploadedSims.length > 0 && (
                      <div className="mt-2">
                        <strong>{values.uploadedSims.length}</strong> SIM
                        {values.uploadedSims.length === 1 ? "" : "s"} loaded.
                      </div>
                    )}
                  </Form.Group>

                  <Modal.Footer>
                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                      onClick={() => {
                        setFieldValue("file", null);
                        setFieldValue("uploadedSims", []);
                        handleToggleModal(false);
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                      onClick={handleSubmit}
                    >
                      Continue
                    </Button>
                  </Modal.Footer>
                </div>
              )}

              {/* STEP 2: Choose Values */}
              {!isLoading && step === 2 && (
                <Form onSubmit={handleSubmit}>
                  {values.values.map((field, index) => (
                    <Form.Group className="mb-3" key={field.customFieldId}>
                      <Form.Label>{field.displayName}</Form.Label>
                      <Form.Control
                        type="text"
                        name={`values[${index}].value`}
                        value={values.values[index].value}
                        onChange={(e) =>
                          setFieldValue(
                            `values[${index}].value`,
                            e.target.value
                          )
                        }
                        placeholder={`Enter value for ${field.displayName}`}
                      />
                    </Form.Group>
                  ))}

                  <Modal.Footer>
                    <Button
                      variant="primary"
                      onClick={() => {
                        // back to Step 1 only when using upload flow
                        if (!selectedSims?.length) setStep(1);
                        else handleToggleModal(false);
                      }}
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                    >
                      Back
                    </Button>

                    <Button
                      type="submit"
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                    >
                      Review Summary
                    </Button>
                  </Modal.Footer>
                </Form>
              )}

              {/* STEP 3: Summary */}
              {!isLoading && step === 3 && (
                <>
                  <p className="mb-3">
                    Please review the changes below. These values will be
                    applied to the SIMs listed.
                  </p>

                  {renderSummaryTable(
                    summarySims(values.uploadedSims, selectedSims),
                    values.values
                  )}

                  <Modal.Footer>
                    <Button
                      variant="primary"
                      onClick={() => setStep(2)}
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                    >
                      Back
                    </Button>

                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                      onClick={handleSubmit}
                    >
                      Confirm & Apply
                    </Button>
                  </Modal.Footer>
                </>
              )}
            </Modal.Body>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default CustomFieldValuesModal;
